import { getParsedSessionStorageItem, setSessionStorageItem } from 'src/services/storage/session';
import { isUndefined } from 'src/utils/typeguards/isUndefined';
import { isServerSide } from 'src/utils/validators/isServerSide';
import { LookupValues } from 'src/utils/types';
import { parseBooleanInString } from './utils/parseBooleanInString';
import { EXCESS_AMPERSAND_REGEX } from './constants';

type QueryParameterManagerConfiguration = {
    // Query parameters to always remove
    alwaysRemove?: string[];
};

const DELIMITERS = {
    HASH: '#',
    QUERY: '?',
} as const;

type Delimiter = LookupValues<typeof DELIMITERS>;

export interface QueryParameterManager {
    parseQueryParameters(): Record<string, string | boolean>;
    clearParametersFromURL(params: string[]): void;
}

export class PersistedQueryParameterManager implements QueryParameterManager {
    store!: Record<string, string | boolean>;
    private storageKey: string;
    private alwaysRemove: string[];

    constructor(storageKey: string, { alwaysRemove }: QueryParameterManagerConfiguration = {}) {
        this.alwaysRemove = alwaysRemove ?? [];
        this.storageKey = storageKey;
        this.hydrateState();
    }

    /**
     * URLSearchParams will convert ?flag into { flag: '' }, but we want to treat it as a boolean flag
     * so this converts '' to true, such that when we parse the query parameters { flag: '' } -> {flag: true}
     */
    private convertEmptyStringToBoolean(value: string) {
        if (value === '') {
            return true;
        }
        return value;
    }

    parseQueryParameters(): Record<string, string | boolean> {
        const searchParameters = this.parseSearchString(this.getSearchString());
        const hashParameters = this.parseHashString(this.getHashString());
        return {
            ...searchParameters,
            ...hashParameters,
        };
    }

    private parseHashString(hashString: string) {
        return Array.from(new URLSearchParams(hashString).entries()).reduce<Record<string, string | boolean>>(
            (acc, [key, value]) => ({
                ...acc,
                [key]: this.convertEmptyStringToBoolean(parseBooleanInString(value)),
            }),
            {}
        );
    }
    private parseSearchString(searchString: string) {
        return Array.from(new URLSearchParams(searchString).entries()).reduce<Record<string, string | boolean>>(
            (acc, [key, value]) => ({
                ...acc,
                [key]: this.convertEmptyStringToBoolean(parseBooleanInString(value)),
            }),
            {}
        );
    }
    getHashString() {
        if (isServerSide() || !globalThis.location.hash) {
            return '';
        }
        const POST_HASH_INDEX = 1;
        return globalThis.location.hash.substring(POST_HASH_INDEX);
    }
    getSearchString() {
        if (isServerSide() || !globalThis.location.search) {
            return '';
        }
        const POST_QUESTION_MARK_INDEX = 1;
        return globalThis.location.search.substring(POST_QUESTION_MARK_INDEX);
    }

    private removeQueryParamsFromHref = (
        href: string,
        delimiter: Delimiter,
        queryString: string,
        paramsToRemove: string[]
    ) => {
        const PARSERS: Record<Delimiter, (url: string) => Record<string, string | boolean>> = {
            [DELIMITERS.HASH]: this.parseHashString,
            [DELIMITERS.QUERY]: this.parseSearchString,
        };
        const parser = PARSERS[delimiter];
        const queryParameters: Record<string, string | boolean> = parser.bind(this)(queryString);
        const applicableParameters = Array.from(new Set([...paramsToRemove, ...this.alwaysRemove])).filter(
            parameter => !isUndefined(queryParameters[parameter])
        ) as (keyof typeof queryParameters)[];

        const hrefWithQueryParametersRemoved = applicableParameters
            .map(param => [param, queryParameters[param]])
            .reduce(
                (acc, [key, value]) =>
                    acc
                        .replace(`${key}=${value}`, '')
                        .replace(`${key}=${globalThis.escape(String(value))}`, '')
                        .replace(`${key}`, ''),
                href
            );
        const removedEveryParameter = Object.entries(queryParameters).length === applicableParameters.length;
        if (removedEveryParameter) {
            // we keep postDelimiter in case of situation like example.com?#something -> example.com#something
            const [preDelimiter, postDelimiter] = hrefWithQueryParametersRemoved.split(delimiter);
            return `${preDelimiter}${postDelimiter}`;
        }
        // we removed a subset of the parameters, and now potentially have errant &
        const [preDelimiter, postDelimiter] = hrefWithQueryParametersRemoved.split(delimiter);
        const sanitizedQueryString = postDelimiter
            .split('&')
            .filter(queryPart => !!queryPart)
            .join('&');
        return `${preDelimiter}${delimiter}${sanitizedQueryString}`;
    };

    getQueryParams() {
        const paramsFromSessionStorage = getParsedSessionStorageItem(this.storageKey) ?? {};
        const inboundParams = this.parseQueryParameters();
        return {
            ...paramsFromSessionStorage,
            ...inboundParams,
        };
    }

    /**
     * Updates store and session storage with URL query parameters
     */
    hydrateState() {
        const queryParams = this.getQueryParams();
        setSessionStorageItem(this.storageKey, queryParams);
        this.store = queryParams;
    }

    /**
     * Deletes a query parameter from client side persisted memory
     */
    deletePersistedParameter(key: string) {
        delete this.store[key];
        setSessionStorageItem(this.storageKey, this.store);
    }

    /**
     * Removes query parameters that should not be persisted in the URL from the URL
     */
    clearParametersFromURL(parametersToRemove: string[]) {
        let targetedHREF = globalThis.location.href;
        const query = this.getSearchString();
        const hash = this.getHashString();

        // https://www.rfc-editor.org/rfc/rfc3986#section-3 Query must come first before hash per specification
        if (query) {
            targetedHREF = this.removeQueryParamsFromHref(targetedHREF, DELIMITERS.QUERY, query, parametersToRemove);
        }

        if (hash) {
            targetedHREF = this.removeQueryParamsFromHref(targetedHREF, DELIMITERS.HASH, hash, parametersToRemove);
        }

        targetedHREF = targetedHREF.replace(EXCESS_AMPERSAND_REGEX, '');

        if (targetedHREF !== globalThis.location.href) {
            // @ts-expect-error
            globalThis.history.replaceState({}, null, targetedHREF);
        }
    }
}
